import React, { useMemo } from 'react';
import { useSelector } from "react-redux";
import { Row, Col } from 'react-bootstrap';
import { Unit, roundOfAcres } from '../../../helpers/unit';
import { toDisplayTimeString } from '../../../helpers/moment-ex';
import { isElasticLoad } from '../../../helpers/common';
import LoadingStyle from '../Common/LoadingStyle';

const Summary = (props) => {
  const crewInfo = useSelector(state => state.dashboard.crew.analyseById.hasOwnProperty(props.crewId) ? state.dashboard.crew.analyseById[props.crewId] : {});
  const summary = crewInfo.summary && crewInfo.summary.payload ? crewInfo.summary.payload : null;
  const isRequestingSummary = crewInfo.summary && crewInfo.summary.isRequesting ? crewInfo.summary.isRequesting : false;
  const isRequestingKpi = crewInfo.elasticWork ? crewInfo.elasticWork.isRequesting : false;
  const isRequestingLoad = crewInfo.elasticLoad ? crewInfo.elasticLoad.isRequesting : false;
  const donut = crewInfo.donut;
  const settings = useSelector(state => state.user.settings);
  const unit = Unit.getInstance();

  const isManualLoad = !isElasticLoad();
  const plannedAcres = summary ? unit.toRoundOfArea(summary.acres.planned) : 0;
  const completedAcres = summary ? unit.toRoundOfArea(summary.acres.completed) : 0;
  const remainingAcres = (plannedAcres > completedAcres) ? (plannedAcres - completedAcres) : 0;
  const avgYieldPerAcre = (summary && summary.load && summary.load.total_capacity >= 0 && completedAcres > 0) ?
    unit.toRoundOfWeight(summary.load.total_capacity / completedAcres) : 0;
  const yieldByTruck = isManualLoad && summary && summary.load && summary.load.total_capacity >= 0 && summary.load.total_loads > 0
    ? unit.toRoundOfWeight(summary.load.total_capacity / summary.load.total_loads)
    : 0;
  const weightShortName = settings ? settings.weightShortName.charAt(0).toUpperCase() + settings.weightShortName.slice(1) : 'Capacity';

  const averageCapacityPerHour = useMemo(() => {
    if (!summary || !donut) return 0;
    const { load } = summary;
    const { actual_work } = donut.payload;

    if (!load || !actual_work || !actual_work.hasOwnProperty('h') || !actual_work.hasOwnProperty('m')) return 0;

    const totalHoursWorked = actual_work.h + (actual_work.m / 60);
    const averageCapacityPerHour = totalHoursWorked > 0 ? load.total_capacity / totalHoursWorked : 0;

    return unit.toRoundOfWeight(averageCapacityPerHour);
  }, [summary, isRequestingLoad, donut]);
  const isTareIncludedLoad = summary && summary.tare && summary.tare.is_tare_included_load;
  const totalLoadCapacity = useMemo(() => {
    if (!summary || isRequestingSummary) return 0;
    const isLoadHistory = summary && summary.load && summary.load.total_capacity;
    const istareHistory = summary && summary.tare && summary.tare.total_capacity;
    if (summary) {
      const loadWeight = (isLoadHistory) ? unit.toRoundOfWeight(isLoadHistory, false) : 0;
      const tareWeight = (istareHistory) ? unit.toRoundOfWeight(istareHistory, false) : 0;
      const netYield = isTareIncludedLoad ? loadWeight : loadWeight - tareWeight;
      return unit.toFixedOf(netYield, 2);
    }

    return 0;
  }, [summary, isRequestingSummary]);

  const tareLoadCapacity = summary && summary.tare ? unit.toRoundOfWeight(summary.tare.total_capacity, false) : 0;
  const totalWeight = unit.toFixedOf(totalLoadCapacity + tareLoadCapacity, 2);

  return <>
    <Row className="m-0">
      <Col className="p-0" lg={4} md={4} sm={4}>
        <div className="value">{isRequestingSummary ? <LoadingStyle /> : plannedAcres}</div>
        <div className="label">{`Planned ${settings.areaShortName}`}</div>
      </Col>
      <Col className="p-0" lg={4} md={4} sm={4}>
        <div className="value">{(isRequestingSummary || isRequestingKpi) ? <LoadingStyle /> : completedAcres}</div>
        <div className="label">{`Completed`}</div>
      </Col>
      <Col className="p-0" lg={4} md={4} sm={4}>
        <div className="value">{(isRequestingSummary || isRequestingKpi) ? <LoadingStyle /> : remainingAcres}</div>
        <div className="label">{`Remaining`}</div>
      </Col>
    </Row>
    <Row className="m-0 py-1"><div className="divider" /></Row>
    <Row className="m-0">
      <Col className="p-0" lg={4} md={4} sm={4}>
        <div className="value">{summary
          ? (isRequestingSummary ? <LoadingStyle /> : toDisplayTimeString(summary.hours.planned))
          : 0}</div>
        <div className="label">Planned Hours</div>
      </Col>
      <Col className="p-0" lg={4} md={4} sm={4}>
        <div className="value">{summary
          ? ((isRequestingSummary || isRequestingKpi) ? <LoadingStyle /> : toDisplayTimeString(summary.hours.completed))
          : 0}</div>
        <div className="label">Completed</div>
      </Col>
      <Col className="p-0" lg={4} md={4} sm={4}>
        <div className="value">{summary
          ? ((isRequestingSummary || isRequestingKpi) ? <LoadingStyle /> : toDisplayTimeString(summary.hours.remaining))
          : 0}</div>
        <div className="label">Remaining</div>
      </Col>
    </Row>
    {isManualLoad && <><Row className="m-0 py-1"><div className="divider" /></Row>
      <Row className="m-0">
        <Col className="p-0" lg={4} md={4} sm={4}>
          <div className="value">{summary
            ? (isRequestingSummary ? <LoadingStyle /> : summary.load.total_loads)
            : 0}</div>
          <div className="label">Total Loads</div>
        </Col>
        <Col className="p-0" lg={4} md={4} sm={4}>
          <div className="value">{summary
            ? (isRequestingSummary ? <LoadingStyle /> : totalLoadCapacity)
            : 0}</div>
          <div className="label">Total Net Weight</div>
          <div className="unit-label">{`(${settings.weightShortName})`}</div>
        </Col>
        <Col className="p-0" lg={4} md={4} sm={4}>
          <div className="value">{(isRequestingSummary || isRequestingKpi) ? <LoadingStyle /> : roundOfAcres(avgYieldPerAcre)}</div>
          <div className="label">{`Avg Yield/${settings.areaShortName}`}</div>
          <div className="unit-label">{`(${settings.weightShortName})`}</div>
        </Col>
        <Col>
          <div className="value">{(donut ? donut.isRequesting : isRequestingLoad) ? <LoadingStyle /> : averageCapacityPerHour}</div>
          <div className="label">{`Avg ${weightShortName}/Hour`}</div>
        </Col>
        <Col className="p-0" lg={4} md={4} sm={4}>
          <div className="value">{summary
            ? (isRequestingSummary ? <LoadingStyle /> : yieldByTruck)
            : 0}</div>
          <div className="label">Yield By Truck</div>
          <div className="unit-label">{`(${settings.weightShortName})`}</div>
        </Col>
        {totalWeight !== totalLoadCapacity ? <Col className="p-0" lg={4} md={4} sm={4}>
          <div className="value">{summary
            ? (isRequestingSummary ? <LoadingStyle /> : totalWeight)
            : 0}</div>
          <div className="label">Total Weight</div>
          <div className="unit-label">{`(${settings.weightShortName})`}</div>
        </Col> : null}

      </Row>
      <Row className="m-0 py-1"><div className="divider" /></Row>
      <Row className="m-0">
        <Col className="p-0" lg={4} md={4} sm={4}>
          <div className="value">{summary
            ? (isRequestingSummary ? <LoadingStyle /> : toDisplayTimeString(summary.load.avg_load_time))
            : 0}</div>
          <div className="label">Avg Load Time</div>
        </Col>
        <Col className="p-0" lg={4} md={4} sm={4}>
          <div className="value">{summary
            ? ((isRequestingSummary || isRequestingLoad) ? <LoadingStyle /> : toDisplayTimeString(summary.load.avg_roundtrip_load_time))
            : 0}</div>
          <div className="label">Avg Roundtrip Load Time</div>
        </Col>
        {(summary && summary.tare && summary.tare.total_loads && summary.tare.total_loads > 0) ?
          <Col className="p-0" lg={4} md={4} sm={4}>
            <div className="value">{summary
              ? ((isRequestingSummary || isRequestingLoad) ? <LoadingStyle /> : summary.tare.total_loads)
              : <></>}</div>
            <div className="label">Tare Loads</div>
          </Col> : <></>}
        {(summary && summary.tare && summary.tare.total_capacity && summary.tare.total_capacity > 0) ?
          <Col className="p-0" lg={4} md={4} sm={4}>
            <div className="value">{summary
              ? ((isRequestingSummary || isRequestingLoad) ? <LoadingStyle /> : unit.toRoundOfWeight(summary.tare.total_capacity, false))
              : <></>}</div>
            <div className="label">Tare Weight {`(${settings.weightShortName})`}</div>
          </Col> : <></>}
      </Row>
    </>}
    {!isManualLoad && <><Row className="m-0 py-1"><div className="divider" /></Row>
      <Row className="m-0">
        <Col className="p-0" lg={4} md={4} sm={4}>
          <div className="value">{summary
            ? ((isRequestingSummary || isRequestingLoad) ? <LoadingStyle /> : summary.load.total_loads)
            : 0}</div>
          <div className="label">Total Loads</div>
        </Col>

        {/* <Col className="p-0" lg={4} md={4} sm={4}>
                    <div className="value">{summary
                        ? ((isRequestingSummary || isRequestingLoad) ? <LoadingStyle /> : summary.load.total_capacity)
                        : 0}</div>
                    <div className="label">Total Weight</div>
                </Col> */}
        <Col className="p-0" lg={4} md={4} sm={4}>
          <div className="value">{summary
            ? ((isRequestingSummary || isRequestingLoad) ? <LoadingStyle /> : toDisplayTimeString(summary.load.avg_roundtrip_load_time))
            : 0}</div>
          <div className="label">Avg Roundtrip Load Time</div>
        </Col>
      </Row>
    </>}
  </>
}

export default Summary;
